'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { authService } from '@/lib/api/auth';
import { useAuth } from '@/lib/hooks/useAuth';
import { Shell } from '@/components/shell/Shell';
import { useShellBootstrap } from '@/lib/shell/useShellBootstrap';
import { useCredits } from '@/lib/shell/useCredits';
import { useOnboardingStatus, ONBOARDING_ROUTE } from '@/lib/shell/useOnboardingStatus';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);

  // The auth context runs the session bootstrap (POST /api/v1/auth/initialize),
  // which syncs the user → org → projects graph the backend reads to resolve
  // scope. We must NOT mount dashboard pages until it finishes: child views fire
  // project-scoped reads (e.g. GET /api/v1/projects) on mount, and if those race
  // ahead of the sync the backend can't resolve the org yet and returns 500 —
  // surfacing as an intermittent 404 shell on first load that "fixes itself" on
  // reload (the graph is warm by then). `authLoading` is that ordering barrier.
  const { isLoading: authLoading } = useAuth();

  // Hydrate the global shell scope (org / user / scope / capabilities), then
  // the credits meter from the data-service once the org is known.
  useShellBootstrap();
  useCredits();

  // Onboarding gate: a signed-in user who hasn't finished onboarding is sent to
  // the standalone /onboarding route. The Shell (sidebar / scope switchers /
  // dashboard) is never rendered below until onboarding is complete, so none of
  // that chrome can flash or be reached beforehand.
  const onboarding = useOnboardingStatus();

  useEffect(() => {
    const authenticated = authService.isAuthenticated();
    if (!authenticated) {
      router.push('/login');
    } else {
      setIsAuthenticated(true);
    }
    setTokenChecked(true);
  }, [router]);

  useEffect(() => {
    if (isAuthenticated && !authLoading && onboarding === 'pending') {
      router.replace(ONBOARDING_ROUTE);
    }
  }, [isAuthenticated, authLoading, onboarding, router]);

  // Hold the loading state until BOTH the local token check AND the auth-context
  // initialization (the /auth/initialize sync) have completed, so no child page
  // can issue a scoped request before the backend can resolve its org.
  if (!tokenChecked || (isAuthenticated && authLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cs-surface-page">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-cs-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-cs-text-muted">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  // Hold the shell until onboarding is known to be complete: 'pending' is mid
  // redirect to /onboarding, 'loading' is the project list still resolving.
  // Either way, render a neutral spinner so the sidebar/dashboard never mount.
  if (onboarding !== 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cs-surface-page">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-cs-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-cs-text-muted">Loading…</p>
        </div>
      </div>
    );
  }

  // The global shell (Module 00): 10-group sidebar, scope trail, command
  // palette, theme/mode — every dashboard screen mounts as its child.
  return <Shell>{children}</Shell>;
}
